import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { isValidDeviceId, sanitizeDeviceId } from '../../services/cognitoService';
import { JoanProfile, linkDevice } from '../../services/joanApi';
import { BRAND, dialogPaperProps, primaryButtonSx } from '../../styles/ui';

interface DeviceIdDialogProps {
  open: boolean;
  onClose: () => void;
  onLinked: (profile: JoanProfile) => void;
}

const textFieldStyle = {
  '& .MuiOutlinedInput-input': { fontFamily: 'monospace', letterSpacing: 1 },
};

const DeviceIdDialog: React.FC<DeviceIdDialogProps> = ({ open, onClose, onLinked }) => {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deviceId = sanitizeDeviceId(value);
  const invalid = !isValidDeviceId(deviceId);
  const looksLikeLetterO = /o/i.test(value);

  const handleClose = () => {
    if (saving) return;
    setValue('');
    setTouched(false);
    setError(null);
    onClose();
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (invalid) return;

    setSaving(true);
    setError(null);
    try {
      const profile = await linkDevice(deviceId);
      setValue('');
      setTouched(false);
      onLinked(profile);
    } catch (err: any) {
      setError(err.message || 'Could not link the device. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const helperText = touched && invalid
    ? `Device ID is 16 characters: digits 0–9 and letters A–F.${looksLikeLetterO ? " Did you type the letter 'O' instead of zero?" : ''}`
    : 'The 16-character ID printed on your Joan device, e.g. 0CA2F400007ADE3A.';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogPaperProps}
    >
      <DialogTitle sx={{ color: BRAND.ink }}>Add your device ID</DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            id="deviceId"
            label="Device ID"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={touched && invalid}
            helperText={helperText}
            disabled={saving}
            inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', spellCheck: false }}
            sx={{
              ...textFieldStyle,
              ...(touched && invalid ? { '& .MuiFormHelperText-root': { color: BRAND.danger } } : {})
            }}
          />
          <Typography variant="body2" sx={{ color: BRAND.muted, mt: 2 }}>
            Once linked, your device ID can only be changed by our{' '}
            <Link component={RouterLink} to="/contact" sx={{ color: 'inherit', fontWeight: 600 }}>support team</Link>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{ color: BRAND.ink, '&:hover': { backgroundColor: BRAND.hover } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={primaryButtonSx}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Link device'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeviceIdDialog;
